import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import { AICoachProvider } from "../AICoachContext";
import { AICoachChatWindow } from "../AICoachUI";
import { usePost } from "../../API";
import { useDebouncedEffect } from "../../Hooks/Debounce";
import { UserInfoContext } from "../../Context";
import { Alert, Icon } from "@aidkitorg/component-library";
import { useInterval } from "../../Util";
import type { CoachGenerationTrace } from "@aidkitorg/types/lib/convoai";
import type { EditableMessageTemplate as SrcEditableMessageTemplate } from "@aidkitorg/types/src/survey";

export function PlaygroundTracePanel({ trace }: { trace: CoachGenerationTrace | null }) {
  if (!trace) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
        No trace captured yet. Send a Playground chat message to inspect the latest generation.
      </div>
    );
  }

  const agentOutputEntries = Object.entries(trace.agentOutputs);

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
      <div className="font-semibold text-slate-900">Latest trace</div>
      <div className="mt-2 space-y-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Delegated agents</div>
          {trace.delegation.length === 0 ? (
            <div className="mt-1 text-slate-500">No delegated agents ran.</div>
          ) : (
            <div className="mt-1 space-y-2">
              {trace.delegation.map((entry, index) => (
                <div key={`${entry.name}-${index}`} className="rounded border border-slate-200 bg-white p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-slate-900">{entry.name}</span>
                    <span className={entry.status === 'ok' ? 'text-green-700' : 'text-red-700'}>
                      {entry.status}
                    </span>
                    <span className="text-slate-500">
                      {entry.templateName} v{entry.templateVersion}
                    </span>
                    <span className="text-slate-500">
                      {entry.attempts} attempt{entry.attempts === 1 ? '' : 's'}, {entry.durationMs}ms
                    </span>
                  </div>
                  {(entry.outputPreview || entry.errorMessage) && (
                    <div className="mt-1 whitespace-pre-wrap text-slate-700">
                      {entry.outputPreview ?? entry.errorMessage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Agent outputs</div>
          {agentOutputEntries.length === 0 ? (
            <div className="mt-1 text-slate-500">No agent outputs captured.</div>
          ) : (
            <div className="mt-1 space-y-2">
              {agentOutputEntries.map(([name, output]) => (
                <div key={name} className="rounded border border-slate-200 bg-white p-2">
                  <div className="font-mono text-slate-900">{name}</div>
                  <div className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap text-slate-700">
                    {output}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {trace.mainAssistantText && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Main assistant response</div>
            <div className="mt-1 max-h-40 overflow-auto rounded border border-slate-200 bg-white p-2 whitespace-pre-wrap text-slate-700">
              {trace.mainAssistantText}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Admin message-template playground: same coach UI as applicants.
 * Chat always uses `/ai/chat/message` with the published `MessageTemplateRef` — the same resolution path
 * as production. Unsaved editor content is persisted via `draft_upsert` only; it does not change what the
 * coach model sees until you publish (so no draft partition on the chat endpoint).
 */
export function MessageTemplatePlaygroundChat({ template, unsavedEdits, onTrace }: {
  template: survey.EditableMessageTemplate;
  /** Snapshot when the page loaded; used to detect unsaved edits. */
  unsavedEdits: boolean;
  onTrace?: (trace: CoachGenerationTrace | null) => void;
}) {
  const { uid } = useContext(UserInfoContext);

  const draftUpsert = usePost("/program/admin/prompts/create", {
    suppressErrorDisplay: true,
    handleErrors() { },
  });

  const [draftName, setDraftName] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<'saving' | 'saved' | 'error' | 'loading'>('saved');

  const updateDraft = useCallback(async () => {
    let templateName = template.name.trim();
    if (unsavedEdits) {
      templateName += `__${uid}_draft`;
      try {
        setDraftStatus('saving');
        const result = await draftUpsert({
          overwrite: true,
          item: {
            ...template,
            name: templateName,
            version: 0
          } as SrcEditableMessageTemplate
        })
        if (result.success) {
          setDraftName(templateName);
          setDraftStatus('saved');
        } else {
          throw new Error();
        }
      } catch (e) {
        setDraftStatus('error');
      }
    } else {
      setDraftName(null);
      setDraftStatus('saved');
    }
  }, [uid, draftUpsert, template, unsavedEdits]);

  useDebouncedEffect(updateDraft, {
    maxWait: 5000,
    timeWindow: 200,
    dependencies: [template, unsavedEdits]
  });

  // re-try automatically if there is an error
  useInterval(() => {
    if (draftStatus === 'error') {
      updateDraft();
    }
  }, 3_000, { runInBackground: false });

  const templateRef = useMemo((): survey.MessageTemplateRef | undefined => {
    if (draftName) {
      return {
        name: draftName,
        version: 0
      }
    } else {
      return template;
    }
  }, [draftName, template.name, template.version]);

  const templateRefKey = useMemo(() => {
    return templateRef ? `${templateRef.name}:${templateRef.version ?? 'latest'}` : 'none';
  }, [templateRef?.name, templateRef?.version]);
  const activeTemplateRefKey = useRef(templateRefKey);
  activeTemplateRefKey.current = templateRefKey;

  const handleTrace = useCallback((trace: CoachGenerationTrace | null) => {
    if (activeTemplateRefKey.current !== templateRefKey) {
      return;
    }

    onTrace?.(trace);
  }, [onTrace, templateRefKey]);

  useEffect(() => {
    onTrace?.(null);
  }, [onTrace, templateRefKey]);


  const draftStatusMessage = useMemo(() => {
    switch (draftStatus) {
      case 'saved':
        return <Alert variant="primary">
          <div className="flex space-x-2">
            <Icon name="CheckCircleIcon" strokeWidth={1.5} size="md" />
            <span>Your changes are driving the current chat</span>
          </div>
        </Alert>;
      case 'saving':
        return <Alert variant="warning">
          <div className="flex space-x-2">
            <Icon name="ArrowPathIcon" className="animate-spin" strokeWidth={1.5} size="md" />
            <span>Saving draft...</span>
          </div>
        </Alert>;
      case 'error':
        return <Alert variant="danger">
          <div className="flex space-x-2">
            <Icon name="ExclamationTriangleIcon" strokeWidth={1.5} size="xl" />
            <span>Unable to save draft, responses may be from a stale template version. will retry shortly.</span>
          </div>
        </Alert>;
    }
  }, [draftStatus]);

  return (
    <div className="flex flex-col flex-1 max-h-[70vh] min-h-[320px]">
      {unsavedEdits && draftStatusMessage}
      <AICoachProvider
        template={templateRef}
        application="playground"
        sessionName="message_template_playground_chat"
        includeTrace
        onTrace={handleTrace}
        surfaceErrors
      >
        <AICoachChatWindow embedded skipTerms onClickMinimize={() => { }} />
      </AICoachProvider>
    </div>
  );
}
